import { useObserveSizeContext } from "@pathwright/ui/src/components/observers/ObserveSizeContext"
import { Box, Spinner, VStack, useDisclosure } from "@chakra-ui/react"
import { keyframes } from "@emotion/react"
import AugmentationLayout from "@pathwright/ux/src/components/augmentation/AugmentationLayout"
import AugmentationPositionedToolbar from "@pathwright/ux/src/components/augmentation/AugmentationPositionedToolbar"
import AugmentationRoutedPanel from "@pathwright/ux/src/components/augmentation/AugmentationRoutedPanel"
import { useMemo } from "react"
import { PathCreatorContextProvider } from "../path/create/PathCreatorContext"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { canEditLibrary } from "../user/permissions"
import { HomeContextProvider, useHomeContext } from "./HomeContext"
import HomeHeader from "./HomeHeader"
import HomeMain from "./HomeMain"
import useHomeAugmentationToolbar from "./augmentations/useHomeAugmentationToolbar"
import AddPathButton from "./components/AddPathButton"
import HomeBackground from "./components/ui/HomeBackground"
import { useHomeRegistrationVars } from "./registration/HomeRegistrationQueryVars"
import { filterJoinedPaths } from "./tips/HomeTips"

const shrinkIn = keyframes`
	from {
		transform: scale(1.15);
	}
	to {
		transform: scale(1);
	}
`

// Max width of the list.
const maxW = "min(500px, calc(100% - 20px))"

function HomePrimary() {
  const pwContext = usePathwrightContext()
  const registrationVariables = useHomeRegistrationVars()
  const { registrations, registrationsQuery } = useHomeContext()

  // Assess whether to auto show tooltips in PathCreatorForm inputs based on
  // whether the user has joined any non-copied paths.
  // If user hasn't joined any paths, then we should auto show tips.
  const autoShowTips = useMemo(
    () => filterJoinedPaths(registrations).length <= 0,
    [registrations]
  )

  // Reload related data after user completes Path Creator Flow.
  // NOTE: we could possibly(?) use the onComplete state prop to update the cache instead.
  async function onAddPathComplete() {
    await registrationsQuery.refetch()
  }

  const { getRectValue } = useObserveSizeContext()
  const alertHeight = getRectValue("alert.height")

  // Requiring the user to be a member of the school (a superuser can edit the library
  // but may not be a member of the school). This is necessary because the AddPathButton
  // uses the assign API under the hood which requires school membership.
  const canAddPath = canEditLibrary(pwContext) && !!pwContext.me.membership

  return (
    <VStack
      w="full"
      minH="100dvh"
      h="full"
      minW="min(500px, 100vw)"
      animation={`${shrinkIn} 500ms ease-out`}
      pb={8}
    >
      <Box
        className="HomeHeaderSticky"
        position="sticky"
        top={`${alertHeight}px`}
        w="100%"
        // Overlay other elements which are z-indexed at 1000.
        zIndex={1011}
        // Since we're z-indexing this element and positioning it sticky,
        // it will overlay elements that the user may wish to interact with.
        // Add pointerEvents "all" to any child element the user may wish to interact with.
        pointerEvents="none"
        minW="min(500px, 100vw)"
      >
        <HomeHeader />
      </Box>
      <VStack pos="relative" w="100%" maxW={maxW} gap={3} zIndex={1001}>
        <HomeMain variables={registrationVariables} />
        {canAddPath && (
          <AddPathButton
            onComplete={onAddPathComplete}
            autoShowTips={autoShowTips}
          />
        )}
      </VStack>
    </VStack>
  )
}

const Home = () => {
  const toolbar = useHomeAugmentationToolbar()
  const disclosure = useDisclosure()
  const navbarHeight = useObserveSizeContext().getRectValue("navbar.height")
  const { loading: isPrimaryLoading } = useHomeContext()

  return (
    <VStack
      className="Home"
      w="100%"
      mt={`${navbarHeight}px`}
      py={2}
      justifyContent="center"
    >
      <HomeBackground />
      {isPrimaryLoading ? (
        <VStack
          align="center"
          w="full"
          p={2}
          minH="50vh"
          justifyContent="center"
        >
          <Spinner size="lg" color="whiteAlpha.900" />
        </VStack>
      ) : (
        <AugmentationLayout
          components={{
            Primary: HomePrimary,
            Panel: AugmentationRoutedPanel,
            Toolbar: AugmentationPositionedToolbar
          }}
          toolbar={toolbar}
          disclosure={disclosure}
          spacing={[40, 10, 10]}
          allowOverlay
          toolbarYieldsToPanel
          panelPosition="far"
          align="right"
        />
      )}
    </VStack>
  )
}

function HomeContainer() {
  const { me } = usePathwrightContext()

  return me ? (
    <PathCreatorContextProvider>
      <HomeContextProvider>
        <Home />
      </HomeContextProvider>
    </PathCreatorContextProvider>
  ) : null
}

export default HomeContainer
